import { FormEvent, useCallback, useMemo } from "react";

import { useDebounce } from "./useDebounce";

/**
 * Normalizes text for search:
 * - Lowercases
 * - Trims and collapses multiple spaces
 */
export function normalizeText(input: string): string {
  return input.toLowerCase().trim().replace(/\s+/g, " ");
}

/**
 * Client-side filtering hook with debounced search and robust matching
 */
export const useClientFilter = <T>(rows: T[], getValue: (row: T) => string) => {
  const [query, debouncedQuery, setQuery] = useDebounce("");

  const handleSearchChange = useCallback(
    (e: FormEvent<HTMLInputElement>): void => {
      setQuery(e.currentTarget.value);
    },
    [setQuery]
  );

  const filtered = useMemo(() => {
    if (!debouncedQuery) return rows;

    const normalizedQuery = normalizeText(debouncedQuery);
    if (!normalizedQuery) return rows;

    const tokens = normalizedQuery.split(" ");

    return rows.filter((row) => {
      const cell = normalizeText(getValue(row));
      // AND semantics: all tokens must be present in the cell value
      return tokens.every((t) => cell.includes(t));
    });
  }, [rows, getValue, debouncedQuery]);

  return {
    query,
    setQuery,
    filtered,
    onSearchChange: handleSearchChange
  };
};
